// src/components/organism/Users/UsersConfirmModalContainer.tsx
import React, { useMemo } from 'react';

import ConfirmModal from '@/components/molecules/ConfirmModal/ConfirmModal';
import { useAppDispatch } from '@/store';
import { fetchOrgUsers } from '@/store/thunks/organization/fetchOrgUsers';
import { toggleUserActiveThunk } from '@/store/thunks/userForm/toggleUserActiveThunk';
import { forceLogoutUserThunk } from '@/store/thunks/userForm/forceLogoutUserThunk';
import { forceLoginResetUserThunk } from '@/store/thunks/userForm/forceLoginResetUserThunk';
import { deleteUserThunk } from '@/store/thunks/userForm/deleteUserThunk';
import { useConfirm } from '@/hooks/useConfirm';
import { setErrorMessage } from '@/store/slices/ui/UISlice';

const UsersConfirmModalContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { confirm, close } = useConfirm();

  const ui = useMemo(() => {
    if ((confirm == null) || confirm.context !== 'users') return null;

    const { kind, row } = confirm;

    const wrap = (thunk: () => Promise<unknown>) => async() => {
      try {
        await thunk();
        await dispatch(fetchOrgUsers());
        close();
      } catch (err) {
        if (err instanceof Error && typeof err.message === 'string') {
          dispatch(setErrorMessage(err.message));
        }
      }
    };

    switch (kind) {
      case 'userToggleActive': {
        const goingTo = row.status === 'Active' ? 'Inactive' : 'Active';
        return {
          title: 'Confirm User Status Change',
          paragraphs: [
            `Please confirm that you wish to make ${row.name} ${goingTo}. ${row.name} will not be able to log into the Portal while inactive.`,
            `${row.name} will be logged out (if currently logged in) and will be advised that there is an issue with the account on the next login attempt.`,
          ],
          onConfirm: wrap(async() => {
            await dispatch(toggleUserActiveThunk({ id: row.id, currentStatus: row.status })).unwrap();
          }),
        };
      }
      case 'userForceLogout':
        return {
          title: 'Confirm Forced Logout',
          paragraphs: [
            `Please confirm that you wish to log ${row.name} out of the Portal.`,
            `${row.name} will be able to log back in using current credentials.`,
          ],
          onConfirm: wrap(async() => {
            await dispatch(forceLogoutUserThunk({ id: row.id })).unwrap();
          }),
        };
      case 'userForceLoginReset':
        return {
          title: 'Confirm Forced Credential Update',
          paragraphs: [
            `Please confirm that you wish to have ${row.name} update login credentials.`,
            `${row.name} will be logged out of the Portal. On next login, ${row.name} will be prompted to update the login credentials.`,
          ],
          onConfirm: wrap(async() => {
            await dispatch(forceLoginResetUserThunk({ id: row.id })).unwrap();
          }),
        };
      case 'userDelete':
        return {
          title: 'Delete User',
          paragraphs: [
            `Please confirm that you wish to delete ${row.name}.`,
            `This will permanently delete ${row.name}’s account. ${row.name} will be logged out and will be unable to log in again.`,
          ],
          onConfirm: wrap(async() => {
            await dispatch(deleteUserThunk({ id: row.id })).unwrap();
          }),
        };
    }
  }, [confirm, dispatch, close]);

  if ((confirm == null) || (ui == null) || confirm.context !== 'users') return null;

  return (
    <ConfirmModal
      open
      title={ui.title}
      paragraphs={ui.paragraphs}
      confirmText="Confirm"
      onClose={close}
      onConfirm={() => { void ui.onConfirm(); }}
    />
  );
};

export default UsersConfirmModalContainer;
